import {
  DevHubBox,
  DevHubButton,
  DevHubDynamicIcon,
  DevHubGrid,
  DevHubMenuItem,
  DevHubSelect,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { PartySearch } from '../../common/PartySearch';
import { ResponsibleParty } from '../../insights/types/RoleTypes';
import { useCGAHierarchyStyles } from '../styles';
import { HierarchyLevel } from '../types/CGAHierarchyTypes';

interface UserSearchSectionProps {
  selectedParty: ResponsibleParty;
  onPartySelect: React.Dispatch<React.SetStateAction<ResponsibleParty>>;
  hierarchyLevel: HierarchyLevel;
  onHierarchyChange: (event: React.ChangeEvent<{ value: unknown }>) => void;
  onGenerateReport: () => void;
  onClear: () => void;
  hasSearched: boolean;
  targetLogin: string;
  loading: boolean;
  partySearchResetKey: number;
}

/**
 * User search section component for CGA Hierarchy Report.
 * Contains the party search input, hierarchy level selector, and action buttons.
 * Allows users to search for a target user, select hierarchy level, and generate or clear the report.
 * @param props - Component props
 * @param props.selectedParty - Currently selected responsible party
 * @param props.onPartySelect - Callback to update selected party
 * @param props.hierarchyLevel - Currently selected hierarchy level
 * @param props.onHierarchyChange - Callback when hierarchy level changes
 * @param props.onGenerateReport - Callback to initiate report generation
 * @param props.onClear - Callback to clear the search and reset state
 * @param props.hasSearched - Whether a search has been performed (controls button states)
 * @param props.targetLogin - Login of the target user (used to determine if clear button should show)
 * @param props.loading - Whether data is currently loading (disables controls)
 * @param props.partySearchResetKey - Key to reset the PartySearch component when clearing search
 * @returns UserSearchSection component
 */
export const UserSearchSection = ({
  selectedParty,
  onPartySelect,
  hierarchyLevel,
  onHierarchyChange,
  onGenerateReport,
  onClear,
  hasSearched,
  targetLogin,
  loading,
  partySearchResetKey,
}: UserSearchSectionProps) => {
  const classes = useCGAHierarchyStyles();

  return (
    <DevHubBox className={classes.userSearchSection}>
      <DevHubBox className={classes.searchRow}>
        <DevHubTypography
          variant="subtitle1"
          style={{ fontWeight: 500, marginBottom: '12px' }}
        >
          Search for a user to generate their hierarchy CGA report.
        </DevHubTypography>

        <DevHubGrid
          container
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
        >
          <DevHubGrid item xs={12} sm={6} md={6}>
            <PartySearch
              key={partySearchResetKey}
              label="Search User"
              selectedParty={selectedParty}
              setSelectedParty={onPartySelect}
              groupEnabled={false}
              context="report"
              required
            />
          </DevHubGrid>

          <DevHubGrid item xs={12} sm={3} md={3}>
            <DevHubSelect
              value={hierarchyLevel}
              label="Hierarchy Level"
              onChange={onHierarchyChange}
              disabled={loading}
              style={{
                width: '70%',
                minWidth: '70px',
                marginLeft: '42px',
              }}
            >
              <DevHubMenuItem value={HierarchyLevel.DIRECT_REPORTS}>
                Direct Reports
              </DevHubMenuItem>
              <DevHubMenuItem value={HierarchyLevel.FULL_REPORTING_HIERARCHY}>
                Full Reporting Hierarchy
              </DevHubMenuItem>
            </DevHubSelect>
          </DevHubGrid>

          <DevHubGrid item xs={12} sm={3} md={3}>
            {hasSearched && targetLogin ? (
              <DevHubButton
                variant="outlined"
                color="secondary"
                onClick={onClear}
                startIcon={<DevHubDynamicIcon iconName="Clear" />}
                size="large"
              >
                Clear
              </DevHubButton>
            ) : (
              <DevHubButton
                variant="contained"
                color="primary"
                onClick={onGenerateReport}
                disabled={!selectedParty?.login || loading}
                startIcon={<DevHubDynamicIcon iconName="Search" />}
                size="large"
              >
                Search
              </DevHubButton>
            )}
          </DevHubGrid>
        </DevHubGrid>
      </DevHubBox>
    </DevHubBox>
  );
};
